import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { IntrospectNode } from '@adornis/introspect/entities/introspect-node.entity.js';
import { getIntrospectCollectionName } from '@adornis/introspect/server/introspection-options.js';
import { EXTRACTION_STEP_LABELS } from './mail-content-extraction-agent/runExtractionByContext.js';

// One clean payload for the "Nachvollziehen" timeline + the live extraction scene:
// the named pipeline steps of the latest extraction run for a mail, with status.
// Returned as a JSON string to avoid registering a dedicated entity for a read-only,
// derived view.
export interface ExtractionStepStatus {
  operation: string;
  startedAt: string | null;
  finishedAt: string | null;
  hasError: boolean;
  // Chain-of-thought: the match decisions (product/company) recorded under this step via
  // addIntrospectMeta — why a candidate was chosen and why the others were rejected. The
  // shape is whatever the matcher wrote (see search-products.ts / correctAndCompleteCompany.ts);
  // passed through verbatim for the timeline to render. Empty for steps without a match decision.
  decisions: unknown[];
}

/**
 * Walks the introspection tree of the latest `extractMailInformation` run for the given
 * mail and returns its named pipeline steps (see EXTRACTION_STEP_LABELS) with timing and
 * error status, ordered as they ran. Empty array (as JSON) when no run exists yet.
 */
export const getExtractionStepsByMail = registerQuery({
  type: () => String,
  operationName: 'getExtractionStepsByMail',
  params: [{ name: 'mailID', type: () => String }],
  resolve: (mailID: string) => {
    return async () => {
      const collection = await getRawCollection<IntrospectNode>(getIntrospectCollectionName());

      // Latest extraction run for this mail.
      const root = await collection
        .find({ operation: 'extractMailInformation', 'args.mailID': mailID })
        .sort({ startedAt: -1 })
        .limit(1)
        .next();
      if (!root?.childIDs?.length) return '[]';

      // The named steps live one level below, under the analyzeMailContent node.
      const level1 = await collection.find({ _id: { $in: root.childIDs } }).toArray();
      const analyze = level1.find(n => n.operation === 'analyzeMailContent') ?? root;
      const stepIDs = analyze.childIDs ?? [];
      if (!stepIDs.length) return '[]';

      const labels = new Set<string>(EXTRACTION_STEP_LABELS);
      const stepNodes = await collection.find({ _id: { $in: stepIDs } }).toArray();

      // Match decisions live in the `meta` of either the step node itself (e.g. the
      // unwrapped requesting-company match) or its children (one correctAndCompleteProduct
      // node per product, the wrapped payment-company match). Fetch the children once and
      // index them so each step can gather its own decisions.
      const childIDs = stepNodes.flatMap(n => n.childIDs ?? []);
      const childNodes = childIDs.length ? await collection.find({ _id: { $in: childIDs } }).toArray() : [];
      // Raw Mongo docs (WithId<IntrospectNode>), not entity instances — read fields structurally.
      // `meta` is persisted as a nested object (not a string), so we can reach into it directly.
      interface RawNode {
        childIDs?: string[] | null;
        meta?: unknown;
      }
      const childById = new Map<string, RawNode>(childNodes.map(c => [c._id, c as RawNode]));

      const collectDecisions = (node: RawNode): unknown[] => {
        const sources = [node, ...(node.childIDs ?? []).map(id => childById.get(id)).filter(Boolean)];
        const seen = new Set<string>();
        const decisions: unknown[] = [];
        for (const src of sources) {
          const meta = src?.meta as { matchDecision?: unknown; quantityCalc?: unknown } | null | undefined;
          const decision = meta?.matchDecision;
          if (decision === null || decision === undefined) continue;
          // Fold the bulk-length → piece-count derivation (written by correctAndCompleteProduct on
          // the same node) into the product decision so the timeline can render it inline.
          if (meta?.quantityCalc != null && typeof decision === 'object') {
            (decision as Record<string, unknown>).quantityCalc = meta.quantityCalc;
          }
          const key = JSON.stringify(decision);
          if (seen.has(key)) continue; // requesting+payment company often resolve to the same decision
          seen.add(key);
          decisions.push(decision);
        }
        return decisions;
      };

      const steps: ExtractionStepStatus[] = stepNodes
        .filter(n => n.operation != null && labels.has(n.operation))
        .map(n => ({
          operation: n.operation as string,
          startedAt: n.startedAt ? new Date(n.startedAt).toISOString() : null,
          finishedAt: n.finishedAt ? new Date(n.finishedAt).toISOString() : null,
          hasError: !!n.errors,
          decisions: collectDecisions(n),
        }))
        .sort((a, b) => (a.startedAt ?? '').localeCompare(b.startedAt ?? ''));

      return JSON.stringify(steps);
    };
  },
});
